import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, StatusBadge, useCart, useToast } from '@kss/ui';
import {
  ArrowRight,
  Bell,
  CreditCard,
  Heart,
  Loader2,
  MapPin,
  Package,
  RefreshCw,
  Truck,
  User as UserIcon,
} from 'lucide-react';
import { useAuth } from '@kss/auth';
import { authService, type SalesOrder } from '@kss/api';
import { reorderFromOrder } from './reorderFromOrder';

const TILES = [
  { to: '/account/orders', label: 'My orders', icon: Package, hint: 'View, cancel, or reorder' },
  { to: '/account/track-order', label: 'Track order', icon: Truck, hint: 'Live shipment timeline' },
  { to: '/account/profile', label: 'Profile', icon: UserIcon, hint: 'Name, email, phone, avatar' },
  { to: '/account/addresses', label: 'Addresses', icon: MapPin, hint: 'Shipping & billing' },
  { to: '/account/payment-methods', label: 'Payment methods', icon: CreditCard, hint: 'Cards, UPI, COD' },
  { to: '/account/wishlist', label: 'Wishlist', icon: Heart, hint: 'Saved for later' },
  { to: '/account/notifications', label: 'Notifications', icon: Bell, hint: 'Email & SMS preferences' },
];

/**
 * Map the live API's numeric `status` enum (and the legacy string
 * variants used by older mocks) to a human label + StatusBadge token.
 *
 * Same mapping as `AccountOrders.tsx` — kept inline here so the
 * overview doesn't depend on an internal export from that page.
 */
function statusLabel(raw: SalesOrder['status']): string {
  if (typeof raw === 'number') {
    if (raw === 5) return 'Cancelled';
    if (raw === 4) return 'Delivered';
    if (raw === 3) return 'Shipped';
    if (raw === 2) return 'Confirmed';
    return 'Pending';
  }
  const v = String(raw ?? '').toLowerCase();
  if (v === 'cancelled' || v === 'canceled') return 'Cancelled';
  if (v === 'delivered') return 'Delivered';
  if (v === 'shipped') return 'Shipped';
  if (v === 'confirmed') return 'Confirmed';
  return 'Pending';
}

export default function AccountOverview() {
  const { user, isAuthenticated } = useAuth();
  const cart = useCart();
  const toast = useToast();
  // The id of the order whose Reorder button is currently spinning.
  // Tracked at the row level so a slow lookup on order A doesn't
  // grey out the Reorder button on order B.
  const [reorderingId, setReorderingId] = useState<string | null>(null);

  async function handleReorder(o: SalesOrder) {
    const id = String(o.salesOrderId ?? '');
    if (!id) return;
    setReorderingId(id);
    try {
      const { added, skipped, total } = await reorderFromOrder(cart, id);
      cart.openDrawer();
      const ref = o.orderNumber ?? id;
      if (added === 0) {
        toast.error(
          'Could not re-add items',
          total === 0
            ? `No items recorded on ${ref}.`
            : 'Those products are no longer available.',
        );
      } else if (skipped > 0) {
        toast.info(
          'Cart updated',
          `Added ${added} of ${total} items from ${ref}. ${skipped} no longer available.`,
        );
      } else {
        toast.success(
          'Cart updated',
          `Added ${added} ${added === 1 ? 'item' : 'items'} from ${ref}.`,
        );
      }
    } catch (e) {
      toast.error('Reorder failed', (e as Error)?.message ?? 'Try again later.');
    } finally {
      setReorderingId(null);
    }
  }

  // Recent orders preview — three most-recent orders for this customer.
  // Backed by `/api/auth/me/orders` (server filters strictly by JWT
  // customerId, so no tenant-scope leakage). Empty + error states are
  // both handled inline so the overview never goes blank.
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await authService.myOrders({ pageSize: 3 });
        const items = Array.isArray(resp?.items) ? (resp.items as SalesOrder[]) : [];
        // Sort newest-first, defensively — backend already sorts but
        // we don't depend on it here.
        items.sort((a, b) => {
          const da = a.orderDate ?? a.createdAt ?? '';
          const db = b.orderDate ?? b.createdAt ?? '';
          return db.localeCompare(da);
        });
        if (!cancelled) setOrders(items.slice(0, 3));
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return (
    <div className="grid gap-4">
      <Card className="p-5">
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          Manage your orders, profile, addresses, and payment methods from one place.
        </p>
      </Card>

      {/* Recent orders — small panel above the tile grid so a returning
          customer immediately sees their latest activity. Hidden when
          the customer has no orders yet (the empty state lives on the
          full /account/orders page). */}
      {(loading || orders.length > 0) && (
        <Card className="p-0">
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-[var(--color-primary)]" />
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Recent orders
              </h2>
            </div>
            <Link
              to="/account/orders"
              className="text-xs font-medium text-[var(--color-primary)] hover:underline"
            >
              View all
            </Link>
          </div>
          <div className="border-t border-[var(--color-border)]">
            {loading && orders.length === 0 ? (
              <div className="grid place-items-center px-5 py-6 text-xs text-[var(--color-text-muted)]">
                Loading recent orders…
              </div>
            ) : (
              <ul className="divide-y divide-[var(--color-border)]">
                {orders.map((o) => {
                  const id = String(o.salesOrderId ?? o.orderNumber ?? '');
                  const orderId = String(o.salesOrderId ?? '');
                  const label = statusLabel(o.status);
                  const date = (o.orderDate ?? o.createdAt ?? '').slice(0, 10);
                  const total = Number(o.totalAmount ?? 0);
                  const isReordering = reorderingId === orderId;
                  return (
                    <li
                      key={id}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--color-bg-card-hover)]"
                    >
                      <Link
                        to={`/account/orders/${id}`}
                        className="flex min-w-0 flex-1 items-center gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                            {o.orderNumber ?? id}
                          </div>
                          <div className="text-[11px] text-[var(--color-text-muted)]">
                            {date || '—'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-[var(--color-text-primary)]">
                            ₹ {total.toLocaleString('en-IN')}
                          </div>
                          <StatusBadge status={label} className="mt-0.5" />
                        </div>
                      </Link>
                      <button
                        type="button"
                        title="Reorder these items"
                        aria-label={`Reorder ${o.orderNumber ?? id}`}
                        disabled={isReordering || !orderId}
                        onClick={(e) => {
                          // The row itself is a Link; stop the click
                          // from bubbling so we don't navigate away.
                          e.preventDefault();
                          e.stopPropagation();
                          handleReorder(o);
                        }}
                        className="inline-flex h-8 items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2.5 text-[11px] font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-card-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isReordering ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Adding…
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-3.5 w-3.5" />
                            Reorder
                          </>
                        )}
                      </button>
                      <ArrowRight className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {TILES.map(({ to, label, icon: Icon, hint }) => (
          <Link
            key={to}
            to={to}
            className="group flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 transition-colors hover:bg-[var(--color-bg-card-hover)]"
          >
            <Icon className="mt-0.5 h-5 w-5 text-[var(--color-primary)]" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-[var(--color-text-primary)]">{label}</p>
              <p className="text-xs text-[var(--color-text-secondary)]">{hint}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-[var(--color-text-muted)] transition-transform group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </div>
  );
}
